import { useState, useEffect, useRef } from 'react'
import { Plus, ArrowLeft, MessageCircle, X, Send } from 'lucide-react'
import { timeAgo } from '../../utils/timeAgo'

const STATUS_CLS = {
  open: 'bg-amber-100 text-amber-700',
  pending: 'bg-blue-100 text-blue-700',
  resolved: 'bg-green-100 text-green-700',
}
const STATUS_LABEL = { open: 'Open', pending: 'Support replied', resolved: 'Resolved' }

// HQ's Inbox lists tickets by subject, so one is still stored — taken from the
// opening message rather than demanded up front, which would mean filling in a
// form to start a conversation.
function subjectFrom(body) {
  const line = body.trim().split('\n')[0].trim()
  if (!line) return 'Support request'
  if (line.length <= 60) return line
  const cut = line.slice(0, 60)
  const lastSpace = cut.lastIndexOf(' ')
  return `${(lastSpace > 30 ? cut.slice(0, lastSpace) : cut).trim()}…`
}

// `inPanel` renders this to fill a fixed-height side panel — flush edges, a
// coloured header bar, and a composer pinned to the bottom — rather than as a
// free-standing card on the Profile page.
export default function SupportPanel({ currentUser, support, onClose, inPanel = false }) {
  const { tickets, messages, fetchMessages, createTicket, sendMessage, markRead } = support
  const [selectedId, setSelectedId] = useState(null)
  const [showNew, setShowNew] = useState(false)
  const [body, setBody] = useState('')
  const [urgent, setUrgent] = useState(false)
  const [reply, setReply] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const endRef = useRef(null)

  const selected = tickets.find((t) => t.id === selectedId) || null
  const thread = messages[selectedId] || []

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }) }, [thread.length])

  function open(t) {
    setSelectedId(t.id)
    fetchMessages(t.id)
    if (t.userUnread) markRead(t.id)
  }

  async function handleCreate() {
    const text = body.trim()
    if (!text) { setError('Type a message first.'); return }
    setBusy(true); setError('')
    const result = await createTicket({ subject: subjectFrom(text), body: text, priority: urgent ? 'high' : 'normal' })
    setBusy(false)
    if (!result.success) { setError(result.message); return }
    setShowNew(false); setBody(''); setUrgent(false)
    open({ id: result.ticketId, userUnread: false })
  }

  async function handleReply() {
    const text = reply.trim()
    if (!text || !selected) return
    setBusy(true)
    const result = await sendMessage(selected.id, text)
    setBusy(false)
    if (result.success) setReply('')
  }

  // ── New conversation ──
  if (showNew) {
    return (
      <div className={`bg-white overflow-hidden flex flex-col ${inPanel ? 'h-full' : 'rounded-2xl shadow'}`}>
        <div className={`flex items-center gap-2.5 px-4 py-3 ${inPanel ? 'bg-blue-600 text-white' : 'border-b border-gray-100'}`}>
          <button onClick={() => { setShowNew(false); setError('') }} aria-label="Back"
            className={`p-1.5 -ml-1 rounded-lg transition ${inPanel ? 'text-white/80 hover:bg-white/10 hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}>
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-bold ${inPanel ? 'text-white' : 'text-gray-800'}`}>New conversation</p>
          </div>
          {inPanel && onClose && (
            <button onClick={onClose} aria-label="Close support"
              className="p-1.5 -mr-1 rounded-lg text-white/80 hover:bg-white/10 hover:text-white transition">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className={`flex-1 px-4 py-5 ${inPanel ? 'overflow-y-auto' : ''}`}>
          <div className="flex gap-2.5">
            <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-blue-50">
              <MessageCircle className="h-4 w-4 text-blue-600" />
            </span>
            <div className="max-w-[85%] rounded-xl rounded-bl-sm bg-gray-100 px-3.5 py-2.5 text-[13px] leading-relaxed text-gray-700">
              Hi {currentUser.name?.split(' ')[0] || 'there'}, how can we help? Tell us what happened and
              what you expected — we&rsquo;ll reply right here.
            </div>
          </div>
          <label className="mt-5 flex items-center gap-2 text-[13px] text-gray-600">
            <input type="checkbox" checked={urgent} onChange={(e) => setUrgent(e.target.checked)}
              className="h-4 w-4 accent-blue-600" />
            This is urgent — it&rsquo;s blocking our daily entries
          </label>
          {error && (
            <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
          )}
        </div>

        <div className="border-t border-gray-100 px-3 py-2.5">
          <div className="flex items-end gap-2">
            <textarea value={body} onChange={(e) => { setBody(e.target.value); setError('') }} rows={1}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleCreate() } }}
              placeholder="Type your message…"
              autoFocus
              className="max-h-28 flex-1 resize-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
            <button onClick={handleCreate} disabled={busy || !body.trim()} aria-label="Send message"
              className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-blue-600 text-white transition hover:bg-blue-700 disabled:opacity-40">
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Thread view ──
  if (selected) {
    return (
      <div className={`bg-white overflow-hidden flex flex-col ${inPanel ? 'h-full' : 'rounded-2xl shadow max-h-[75vh]'}`}>
        <div className={`flex items-center gap-2.5 px-4 py-3 ${inPanel ? 'bg-blue-600 text-white' : 'border-b border-gray-100'}`}>
          <button onClick={() => setSelectedId(null)} aria-label="Back to tickets"
            className={`p-1.5 -ml-1 rounded-lg transition ${inPanel ? 'text-white/80 hover:bg-white/10 hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}>
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-bold truncate ${inPanel ? 'text-white' : 'text-gray-800'}`}>{selected.subject}</p>
            <p className={`text-xs ${inPanel ? 'text-blue-100' : 'text-gray-400'}`}>Opened {timeAgo(selected.createdAt)}</p>
          </div>
          {inPanel ? (
            onClose && (
              <button onClick={onClose} aria-label="Close support"
                className="p-1.5 -mr-1 rounded-lg text-white/80 hover:bg-white/10 hover:text-white transition">
                <X className="w-4 h-4" />
              </button>
            )
          ) : (
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_CLS[selected.status]}`}>
              {STATUS_LABEL[selected.status]}
            </span>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {thread.map((m) => (
            <div key={m.id} className={`max-w-[80%] rounded-xl px-3.5 py-2.5 text-[13px] leading-relaxed ${
              m.senderRole === 'customer'
                ? 'ml-auto bg-blue-600 text-white rounded-br-sm'
                : 'bg-gray-100 text-gray-700 rounded-bl-sm'
            }`}>
              <p className="text-[11px] font-semibold opacity-60 mb-0.5">
                {m.senderRole === 'customer' ? (m.senderId === currentUser.id ? 'You' : m.senderName) : 'PetroTrack Support'} · {timeAgo(m.createdAt)}
              </p>
              <p className="whitespace-pre-wrap">{m.body}</p>
            </div>
          ))}
          <div ref={endRef} />
        </div>

        <div className="border-t border-gray-100 px-3 py-2.5">
          {selected.status === 'resolved' && (
            <p className="mb-2 px-1 text-xs text-gray-400">This ticket is resolved — replying will reopen it.</p>
          )}
          <div className="flex items-end gap-2">
            <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={1}
              onKeyDown={(e) => {
                // Enter sends, Shift+Enter starts a new line — chat convention.
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply() }
              }}
              placeholder="Type your message…"
              className="flex-1 max-h-28 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-blue-500 resize-none" />
            <button onClick={handleReply} disabled={busy || !reply.trim()} aria-label="Send reply"
              className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-blue-600 text-white transition hover:bg-blue-700 disabled:opacity-40">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── List view ──
  return (
    <div className={inPanel ? 'bg-white h-full flex flex-col overflow-hidden' : 'space-y-3'}>
      <div className={`flex items-center justify-between ${inPanel ? 'bg-blue-600 px-4 py-3.5' : ''}`}>
        <div className={inPanel ? 'min-w-0' : ''}>
          <h2 className={`text-lg font-bold ${inPanel ? 'text-white leading-none' : 'text-gray-800'}`}>Support</h2>
          {inPanel && <p className="mt-1 text-xs text-blue-100">We usually reply within a working day.</p>}
        </div>
        <div className="flex items-center gap-1.5">
          {!inPanel && (
            <button onClick={() => setShowNew(true)}
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3.5 py-2 rounded-lg transition">
              <Plus className="w-3.5 h-3.5" /> New ticket
            </button>
          )}
          {onClose && (
            <button onClick={onClose} aria-label="Close support"
              className={`p-1.5 rounded-lg transition ${inPanel ? '-mr-1 text-white/80 hover:bg-white/10 hover:text-white' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}>
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className={inPanel ? 'flex-1 overflow-y-auto' : 'contents'}>
      {tickets.length === 0 ? (
        <div className={`text-center ${inPanel ? 'px-6 py-12' : 'bg-white rounded-2xl shadow px-6 py-12'}`}>
          <MessageCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-semibold text-gray-600">Need a hand?</p>
          <p className="text-xs text-gray-400 mt-1 mb-4">
            Open a ticket and the PetroTrack team will reply right here.
          </p>
          <button onClick={() => setShowNew(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-4 py-2 rounded-lg transition">
            Contact support
          </button>
        </div>
      ) : (
        <div className={inPanel ? '' : 'bg-white rounded-2xl shadow overflow-hidden'}>
          {tickets.map((t) => (
            <button key={t.id} onClick={() => open(t)}
              className="w-full text-left px-4 py-3.5 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition">
              <div className="flex items-center gap-2">
                {t.userUnread && <span className="w-2 h-2 rounded-full bg-blue-500 flex-none" />}
                <p className="flex-1 text-sm font-semibold text-gray-800 truncate">{t.subject}</p>
                <span className="text-[11px] text-gray-400">{timeAgo(t.lastMessageAt)}</span>
              </div>
              <span className={`inline-block mt-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_CLS[t.status]}`}>
                {STATUS_LABEL[t.status]}
              </span>
            </button>
          ))}
        </div>
      )}
      </div>

      {inPanel && tickets.length > 0 && (
        <div className="border-t border-gray-100 p-3">
          <button onClick={() => setShowNew(true)}
            className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700">
            <Plus className="w-4 h-4" /> New ticket
          </button>
        </div>
      )}

    </div>
  )
}
